/**
 * Häkchen „Kunde benachrichtigen“ beim (erneuten) Lösen eines Tickets — Statusmails, Regel 5
 * (Entscheidung Kevin 26.10.2026, backend/src/services/ticketStatusMail.ts).
 *
 * Vorbelegt mit dem Vorschlag des Servers (erstes Lösen und neues Anliegen mailen, Korrektur ohne
 * neuen Kundenkontakt nicht). Angeboten wird es nur, wenn das Ticket schon einmal erledigt war —
 * beim ersten Lösen gibt es nichts zu entscheiden. Was der Techniker davon abweichend wählt, steht
 * auf dem Server im Protokoll.
 */
import { Checkbox, Stack, Text } from '@mantine/core';
import type { StatusMailPreview } from '../lib/api/domains/weclapp';

const SOURCE_LABEL: Record<NonNullable<StatusMailPreview['lastCustomerContact']>['source'], string> = {
  MAIL: 'Mail',
  PORTAL_COMMENT: 'Kundenportal',
  CALL: 'Anruf',
  WHATSAPP: 'WhatsApp',
};

function berlin(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Begründung des Vorschlags in einem Satz: seit wann erledigt, und ob der Kunde sich seitdem gemeldet hat. */
export function statusMailReason(preview: StatusMailPreview): string {
  const since = preview.resolvedAt ? `Zuletzt erledigt am ${berlin(preview.resolvedAt)}` : 'Schon einmal erledigt';
  const contact = preview.lastCustomerContact
    ? `der Kunde hat sich seitdem gemeldet (${SOURCE_LABEL[preview.lastCustomerContact.source]}, ${berlin(preview.lastCustomerContact.at)})`
    : 'seitdem keine Nachricht vom Kunden';
  return `${since}, ${contact}.`;
}

export function StatusMailCheckbox({
  preview,
  checked,
  onChange,
}: {
  preview: StatusMailPreview;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Stack gap={4}>
      <Checkbox
        label="Kunde per weclapp-Statusmail benachrichtigen"
        checked={checked}
        onChange={(e) => {
          const next = e.currentTarget.checked;
          onChange(next);
        }}
      />
      <Text size="xs" c="dimmed">
        {statusMailReason(preview)} Vorschlag: {preview.decision.notify ? 'benachrichtigen' : 'ohne Mail'}.
      </Text>
      {checked !== preview.decision.notify && (
        <Text size="xs" c="orange">
          Abweichend vom Vorschlag — wird im Protokoll vermerkt.
        </Text>
      )}
      {preview.templatesDisabled === true && (
        <Text size="xs" c="yellow">
          Die Mailvorlagen sind an diesem Ticket ausgeschaltet — weclapp verschickt auch mit Häkchen keine
          Statusmail.
        </Text>
      )}
    </Stack>
  );
}
